import { useCallback, useMemo } from 'react';
import { Form } from 'antd';
import { useQuery } from '@apollo/client';
import type { NamePath } from '@xbcb/ui-types';
import {
  ComplianceCheck,
  ComplianceCheckStatus,
  ComplianceCheckType,
  GetComplianceRecommendationDocument,
} from '__generated__/graphql';
import {
  getLatestComplianceCheck,
  parseComplianceCheckRequestId,
} from 'libs/complianceChecks';
import { DEFAULT_DESTINATION_COUNTRY_CODE } from 'constants/complianceDefaults';
import { clearFields } from 'libs/clearFields';

export type PgaDisclaimRecommendationView =
  | 'none'
  | 'loading'
  | 'error'
  | 'recommended'
  | 'accepted'
  | 'declined';

export interface ComplianceRecommendationDetails {
  loading: boolean;
  disclaimerCode?: string;
  reason?: string;
}

interface UseComplianceRecommendationParams {
  /** Line-item path that holds `complianceChecks`. */
  lineNamePath: NamePath;
  /** Absolute PGA path, e.g. [...line, 'tariffs', idx, 'pga', flag]. */
  fullNamePath: NamePath;
  /** The PGA flag this tab represents (== tariffFlagCode). */
  pgaFlag: string;
  destinationCountryCode?: string;
}

export interface ComplianceRecommendationState {
  status?: ComplianceCheckStatus;
  view: PgaDisclaimRecommendationView;
  details: ComplianceRecommendationDetails;
  accept: () => void;
  decline: () => void;
}

// Drives the PGA Disclaim AI accept/decline flow for a single tariff flag. The
// verdict is a PGA_DISCLAIM compliance check whose `requestId` encodes
// `batchId#identifier#flag`; we match this tab's flag, parse the identifier, and
// query with the flag as `tariffFlagCode`. Accept/decline persist the decision
// back into the line's complianceChecks so it survives a reload.
export const useComplianceRecommendation = ({
  lineNamePath,
  fullNamePath,
  pgaFlag,
  destinationCountryCode = DEFAULT_DESTINATION_COUNTRY_CODE,
}: UseComplianceRecommendationParams): ComplianceRecommendationState => {
  const form = Form.useFormInstance();
  const linePath = lineNamePath as (string | number)[];
  const pgaPath = fullNamePath as (string | number)[];

  const complianceChecks = Form.useWatch(
    [...linePath, 'complianceChecks'],
    form,
  ) as ComplianceCheck[] | undefined;

  const check = getLatestComplianceCheck(
    complianceChecks,
    ComplianceCheckType.PGA_DISCLAIM,
    (c) => parseComplianceCheckRequestId(c.requestId).flag === pgaFlag,
  );
  const status = check?.status ?? undefined;
  const { identifier } = parseComplianceCheckRequestId(check?.requestId);

  const isTerminal =
    status === ComplianceCheckStatus.FAILED ||
    status === ComplianceCheckStatus.COMPLETED;
  const isAccepted = status === ComplianceCheckStatus.ACCEPTED;

  // Only fetch once there's a verdict to act on (terminal or restored ACCEPTED).
  const shouldFetch = Boolean(identifier) && (isTerminal || isAccepted);
  const { data, loading, error } = useQuery(
    GetComplianceRecommendationDocument,
    {
      variables: {
        identifier: identifier as string,
        tariffFlagCode: pgaFlag,
        destinationCountryCode,
      },
      skip: !shouldFetch,
    },
  );

  const payload = data?.getComplianceRecommendation;
  const result = payload?.result;

  const details = useMemo<ComplianceRecommendationDetails>(
    () => ({
      loading: shouldFetch && loading,
      disclaimerCode: result?.disclaimerCode ?? undefined,
      reason: result?.reason ?? undefined,
    }),
    [shouldFetch, loading, result],
  );

  const hasRecommendation =
    payload?.availability === 'AVAILABLE' && Boolean(result?.isEligible);

  const view: PgaDisclaimRecommendationView = isAccepted
    ? 'accepted'
    : status === ComplianceCheckStatus.DECLINED
    ? 'declined'
    : isTerminal
    ? details.loading
      ? 'loading'
      : error
      ? 'error'
      : hasRecommendation
      ? 'recommended'
      : 'none'
    : 'none';

  const setStatusForFlag = useCallback(
    (nextStatus: ComplianceCheckStatus) => {
      const checks =
        (form.getFieldValue([...linePath, 'complianceChecks']) as
          | ComplianceCheck[]
          | undefined) ?? [];
      const idx = checks.findIndex(
        (c) =>
          c.type === ComplianceCheckType.PGA_DISCLAIM &&
          parseComplianceCheckRequestId(c.requestId).flag === pgaFlag,
      );
      if (idx === -1) return;
      form.setFields([
        {
          name: [...linePath, 'complianceChecks'],
          value: checks.map((c, i) =>
            i === idx ? { ...c, status: nextStatus } : c,
          ),
        },
      ]);
    },
    [form, linePath, pgaFlag],
  );

  const accept = useCallback(() => {
    form.setFields([{ name: [...pgaPath, 'disclaim'], value: true }]);
    // NHTSA (DT1) requires program code OFF when disclaimed.
    if (pgaFlag === 'DT1') {
      form.setFields([{ name: [...pgaPath, 'programCode'], value: 'OFF' }]);
    }
    clearFields({
      form,
      fullNamePath: pgaPath,
      exclude: ['agencyCode', 'processingCode', 'programCode', 'disclaim'],
    })();
    if (details.disclaimerCode) {
      form.setFields([
        { name: [...pgaPath, 'disclaimerCode'], value: details.disclaimerCode },
      ]);
    }
    setStatusForFlag(ComplianceCheckStatus.ACCEPTED);
  }, [form, pgaPath, pgaFlag, details.disclaimerCode, setStatusForFlag]);

  const decline = useCallback(() => {
    setStatusForFlag(ComplianceCheckStatus.DECLINED);
  }, [setStatusForFlag]);

  // A COMPLETED check with no eligible recommendation lands on view 'none'.
  // Don't surface COMPLETED to the fallback banner in that case — it would show
  // the "requirement disclaimed by AI" success even though nothing was
  // disclaimed. IN_PROGRESS ("checking") and FAILED ("manual review") banners
  // are still meaningful and pass through.
  const bannerStatus =
    view === 'none' && status === ComplianceCheckStatus.COMPLETED
      ? undefined
      : status;

  return { status: bannerStatus, view, details, accept, decline };
};
